import React, { useCallback, useEffect, useRef, useState } from "react";
import { Pill } from "../../../common/DataList";
import ModaleCorrection from "./ModaleCorrection";
import ModaleOrientation from "./ModaleOrientation";
import {
  etapesDecision,
  libelleAccompagnement,
  ligneAccompagnement,
  ligneOrientationImpossible,
  lignesCorrections,
  nomAccompagnement,
} from "./accompagnement";
import { voirOrientationOperateur, voirSousOrientation } from "./sousOrientationApi";
import "./sous-orientation.scss";

const ICONES = { ok: "fa-check-circle", na: "fa-minus-circle", partiel: "fa-exclamation-circle", avenir: "fa-circle", caf: "fa-arrow-right" };
const COURT = { TOTAL: "total", PARTIEL: "partiel" };

// ============================================================
// Encart « Accompagnement » de l'en-tête du dossier (CE-1370, maquettes 11 et
// 12) : à qui l'allocataire est confié et dans quel accompagnement, pourquoi ;
// « Orienter vers un opérateur » quand il est accompagné par la Caf, « Modifier »
// quand il est confié à un opérateur (CE-1414). L'API dit qui peut quoi : `choix`
// de la sous-orientation, et la réponse de l'orientation (`null` sinon). Rien
// sans sous-orientation, sauf pour qui peut orienter.
// ============================================================
const EncartAccompagnement = ({ allocataireId, nomAllocataire, onCorrigee = () => {} }) => {
  const [so, setSo] = useState(null);
  const [orientation, setOrientation] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);
  const [ouverte, setOuverte] = useState(null);
  const [focusEnAttente, setFocusEnAttente] = useState(false);
  const refBouton = useRef(null);
  const refEncart = useRef(null);
  const monte = useRef(true);

  const charger = useCallback(() => {
    setChargement(true);
    setErreur(false);
    Promise.all([voirSousOrientation(allocataireId), voirOrientationOperateur(allocataireId)]).then(
      ([lue, orientable]) => {
        if (!monte.current) return;
        setSo(lue);
        setOrientation(orientable);
      },
      () => monte.current && setErreur(true)
    ).finally(() => monte.current && setChargement(false));
  }, [allocataireId]);

  useEffect(() => {
    monte.current = true;
    charger();
    return () => {
      monte.current = false;
    };
  }, [charger]);

  // Le focus revient au bouton de l'encart une fois l'encart rendu ; sans bouton, à l'encart lui-même.
  useEffect(() => {
    if (!focusEnAttente) return;
    setFocusEnAttente(false);
    const cible = refBouton.current || refEncart.current;
    if (cible) cible.focus();
  }, [focusEnAttente]);

  const fermer = () => {
    setOuverte(null);
    setFocusEnAttente(true);
  };

  const apres = (relue) => {
    if (!monte.current) return;
    // La modale se ferme d'abord : hors d'un évènement React, chaque mise à jour rend aussitôt (React 17).
    setOuverte(null);
    setSo(relue);
    onCorrigee(relue);
    // Rendu à la Caf, il redevient orientable : le focus attend les choix relus, et le bouton qui va avec.
    if (relue && !relue.operateur) {
      const rendreFocus = () => monte.current && setFocusEnAttente(true);
      voirOrientationOperateur(allocataireId).then((orientable) => {
        if (!monte.current) return;
        setOrientation(orientable);
        rendreFocus();
      }, rendreFocus);
    } else {
      setOrientation(null);
      setFocusEnAttente(true);
    }
  };

  if (chargement) {
    return (
      <div className="accompagnement" aria-busy="true">
        <p className="accompagnement__chargement">Chargement de l'accompagnement…</p>
      </div>
    );
  }
  if (erreur) {
    return (
      <div className="accompagnement">
        <p className="accompagnement__erreur" role="alert">
          L'accompagnement n'a pas pu être chargé.{" "}
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={charger}>
            Réessayer
          </button>
        </p>
      </div>
    );
  }
  if (!so && !orientation) return null;

  const corrections = so ? lignesCorrections(so) : [];
  const etapes = so ? etapesDecision(so) : [];
  const regime = so ? libelleAccompagnement(so) : null;
  const peutModifier = !!(so && so.operateur && so.choix && so.choix.length > 0);
  const aLaCaf = !so || !so.operateur;
  const impossible = aLaCaf && orientation ? ligneOrientationImpossible(orientation) : null;
  const peutOrienter = aLaCaf && !!orientation && !impossible;
  const origine = so && so.origine === "MANUELLE"
    ? `Orientation par un agent : ${so.decision.operateur ? so.decision.operateur.nom : "Caf"}, accompagnement ${COURT[so.decision.accompagnement] || "total"}`
    : `Décision automatique : ${so && so.decision.operateur ? so.decision.operateur.nom : "Caf"}`;

  return (
    <section className="accompagnement" aria-labelledby="accompagnement-titre" ref={refEncart} tabIndex={-1}>
      <div className="accompagnement__label" id="accompagnement-titre">
        Accompagnement
      </div>
      <div className="accompagnement__valeur">
        {so ? nomAccompagnement(so) : "Caf"}
        {corrections.length > 0 && (
          <Pill tone="info" icon="fa-pen">
            Modifiée
          </Pill>
        )}
      </div>
      {regime && <p className="accompagnement__regime">{regime}</p>}
      <p className="accompagnement__ligne">{so ? ligneAccompagnement(so) : "Aucune sous-orientation enregistrée."}</p>
      {(etapes.length > 0 || corrections.length > 0) && (
        <details className="accompagnement__pourquoi">
          <summary>
            <i className="fa fa-chevron-right" aria-hidden="true" />
            Pourquoi ?
          </summary>
          {so.version && <p className="accompagnement__version">{so.version.libelle}</p>}
          {etapes.length > 0 && (
            <ul className="accompagnement__etapes">
              {etapes.map((etape) => (
                <li key={etape.texte} className={`accompagnement__etape accompagnement__etape--${etape.etat}`}>
                  <i className={`fa ${ICONES[etape.etat]}`} aria-hidden="true" />
                  <span>{etape.texte}</span>
                </li>
              ))}
            </ul>
          )}
          {corrections.length > 0 && (
            <>
              <p className="accompagnement__sous-titre">{origine}</p>
              <ul className="accompagnement__corrections">
                {corrections.map((ligne, i) => (
                  // Deux modifications peuvent avoir le même texte ; la liste ne fait que s'allonger.
                  <li key={i}>{ligne}</li>
                ))}
              </ul>
            </>
          )}
        </details>
      )}
      {impossible && <p className="accompagnement__aide">{impossible}</p>}
      {peutModifier && (
        <button type="button" className="btn btn-ghost btn-sm accompagnement__corriger" ref={refBouton} onClick={() => setOuverte("modifier")}>
          Modifier
        </button>
      )}
      {peutOrienter && (
        <button type="button" className="btn btn-primary btn-sm accompagnement__corriger" ref={refBouton} onClick={() => setOuverte("orienter")}>
          Orienter vers un opérateur
        </button>
      )}
      {ouverte === "modifier" && (
        <ModaleCorrection allocataireId={allocataireId} nomAllocataire={nomAllocataire} so={so} onFermer={fermer} onCorrigee={apres} />
      )}
      {ouverte === "orienter" && (
        <ModaleOrientation
          allocataireId={allocataireId}
          nomAllocataire={nomAllocataire}
          orientation={orientation}
          onFermer={fermer}
          onOrientee={apres}
        />
      )}
    </section>
  );
};

export default EncartAccompagnement;
